/**
 * R6.3-B — real telemetry history for the Sector Detail charts.
 *
 * Chart series are built from `GET /iot/nodes/{id}/telemetry` readings of the
 * sector's OWN nodes, resolved through `SectorOperationalModel` provenance
 * (`soilSensor` / `pressureSensor` / `controller`). There is no cross-sector or
 * cross-site borrowing, and no synthesised point: if a sector has no assigned
 * sensor, or the read fails, the series is empty and the chart renders its
 * truthful unavailable state.
 *
 * The one transformation applied is BUCKETING. Pressure and flow can come from
 * two different physical nodes reporting on independent schedules, so raw
 * timestamps rarely coincide and a naive merge would leave every line as
 * isolated invisible dots. Readings are therefore grouped into fixed time
 * buckets, and the LAST real reading in each bucket is plotted. Values are never
 * interpolated, averaged, resampled or carried forward — a bucket with no
 * reading for a metric stays undefined so the line breaks honestly.
 *
 * Pure functions only — no React, no network, no side effects.
 */

import type { TelemetryPoint } from '../../lib/operationalOverview';
import {
  getFlow,
  getPressure,
  getSoilMoisture,
} from '../../lib/liveSensorMetrics';
import { lpmToM3h } from './agriSafeDashboardAdapter';

export type FeedbackRange = '24h' | '48h' | '7d' | '30d' | 'custom';

export interface DateWindow {
  start: string;
  end: string;
}

/** Agronomic reading zones for the soil chart background. Presentation only. */
export const SOIL_MOISTURE_ZONES = [
  { key: 'danger', label: 'Danger', min: 0, max: 20, color: '#ef4444' },
  { key: 'warning', label: 'Attention', min: 20, max: 35, color: '#f59e0b' },
  { key: 'comfort', label: 'Confort', min: 35, max: 60, color: '#22c55e' },
  { key: 'excess', label: 'Excès', min: 60, max: 100, color: '#3b82f6' },
] as const;

/** The telemetry API exposes a rolling `hours` window only, capped at 720. */
export const MAX_HISTORY_HOURS = 720;

const iso = (date: Date): string => date.toISOString().slice(0, 10);

export const getDefaultDateWindow = (now: number = Date.now()): DateWindow => ({
  start: iso(new Date(now - 7 * 24 * 3600_000)),
  end: iso(new Date(now)),
});

const RANGE_MS: Record<Exclude<FeedbackRange, 'custom'>, number> = {
  '24h': 24 * 3600_000,
  '48h': 48 * 3600_000,
  '7d': 7 * 24 * 3600_000,
  '30d': 30 * 24 * 3600_000,
};

export interface ResolvedWindow {
  startMs: number;
  endMs: number;
  /** Rolling window to request from the API, clamped to what it can serve. */
  hours: number;
  /** True when the request was clamped, so the UI can say so rather than lie. */
  truncated: boolean;
  /** False when a custom date interval is malformed or reversed. */
  valid: boolean;
}

/**
 * Resolve a range selection into an API request window.
 *
 * A custom window reaching further back than the API can serve is CLAMPED and
 * flagged, never silently widened or back-filled. An invalid custom interval is
 * rejected with an empty window; consumers must keep history/export unavailable
 * until the operator corrects it.
 */
export function resolveHistoryWindow(
  range: FeedbackRange,
  window: DateWindow | undefined,
  now: number = Date.now(),
): ResolvedWindow {
  if (range !== 'custom') {
    const startMs = now - RANGE_MS[range];
    return {
      startMs,
      endMs: now,
      hours: RANGE_MS[range] / 3600_000,
      truncated: false,
      valid: true,
    };
  }

  const startMs = window ? new Date(`${window.start}T00:00:00`).getTime() : NaN;
  const endMs = window ? new Date(`${window.end}T23:59:59`).getTime() : NaN;
  if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
    // start > end guarantees every timestamp is excluded by downstream filters.
    // `hours` remains a harmless positive value for hook signatures; callers
    // must pass a null node while valid=false so no telemetry request is made.
    return { startMs: 1, endMs: 0, hours: 0, truncated: false, valid: false };
  }

  const spanHours = Math.ceil((now - startMs) / 3600_000);
  const hours = Math.min(Math.max(spanHours, 1), MAX_HISTORY_HOURS);
  return {
    startMs,
    endMs,
    hours,
    truncated: spanHours > MAX_HISTORY_HOURS,
    valid: true,
  };
}

export interface SectorHistoryPoint {
  timestamp: string;
  soilMoisture_percent?: number;
  pressure_bar?: number;
  flow_m3h?: number;
}

/**
 * Bucket width chosen so independent nodes land together without reshaping data.
 *
 * Buckets are epoch-aligned, so two readings a minute apart can still straddle a
 * boundary and stay separate. That is deliberate: aligning them would mean
 * moving a real measurement in time.
 */
export function bucketMsFor(spanMs: number): number {
  const hours = spanMs / 3600_000;
  if (hours <= 48) return 10 * 60_000; // 10 min
  if (hours <= 7 * 24) return 60 * 60_000; // 1 h
  return 6 * 60 * 60_000; // 6 h
}

const timestampMs = (point: TelemetryPoint): number | null => {
  if (!point.ts) return null;
  const parsed = Date.parse(point.ts);
  return Number.isFinite(parsed) ? parsed : null;
};

type MetricKey = 'soilMoisture_percent' | 'pressure_bar' | 'flow_m3h';

interface SeriesSpec {
  series: TelemetryPoint[];
  key: MetricKey;
  read: (point: TelemetryPoint) => number | undefined;
}

/**
 * Merge one or more per-node series into chart points.
 *
 * Every emitted value is a real reading from the given node's own telemetry.
 * Buckets with no reading for a metric leave that key undefined.
 */
export function buildSectorHistory(
  specs: SeriesSpec[],
  window: { startMs: number; endMs: number },
): SectorHistoryPoint[] {
  const bucketMs = bucketMsFor(window.endMs - window.startMs);
  const buckets = new Map<number, SectorHistoryPoint>();

  for (const spec of specs) {
    // Chronological order guarantees "last reading in the bucket" is the latest.
    const ordered = [...spec.series].sort(
      (a, b) => (timestampMs(a) ?? 0) - (timestampMs(b) ?? 0),
    );
    for (const point of ordered) {
      const ms = timestampMs(point);
      if (ms === null || ms < window.startMs || ms > window.endMs) continue;
      const value = spec.read(point);
      if (value === undefined || !Number.isFinite(value)) continue;

      const bucketStart = Math.floor(ms / bucketMs) * bucketMs;
      const existing = buckets.get(bucketStart);
      if (existing) {
        existing[spec.key] = value;
      } else {
        buckets.set(bucketStart, {
          timestamp: new Date(bucketStart).toISOString(),
          [spec.key]: value,
        });
      }
    }
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a - b)
    .map(([, point]) => point);
}

/** Soil-moisture series from the sector's own soil sensor. */
export function buildSoilHistory(
  series: TelemetryPoint[],
  window: { startMs: number; endMs: number },
): SectorHistoryPoint[] {
  return buildSectorHistory(
    [{ series, key: 'soilMoisture_percent', read: (p) => getSoilMoisture(p.measures ?? undefined) }],
    window,
  );
}

/**
 * Hydraulic series from the sector's own pressure and flow nodes.
 *
 * Volume has no canonical Agri Safe source, so no volume key is ever emitted —
 * the chart's volume line renders nothing rather than a fabricated total.
 */
export function buildHydraulicHistory(
  pressureSeries: TelemetryPoint[],
  flowSeries: TelemetryPoint[],
  window: { startMs: number; endMs: number },
): SectorHistoryPoint[] {
  return buildSectorHistory(
    [
      { series: pressureSeries, key: 'pressure_bar', read: (p) => getPressure(p.measures ?? undefined) },
      {
        series: flowSeries,
        key: 'flow_m3h',
        read: (p) => {
          const lpm = getFlow(p.measures ?? undefined);
          return lpm === undefined ? undefined : lpmToM3h(lpm);
        },
      },
    ],
    window,
  );
}

export function formatHistoryAxis(isoTimestamp: string, range: FeedbackRange): string {
  const date = new Date(isoTimestamp);
  if (Number.isNaN(date.getTime())) return '';
  if (range === '24h' || range === '48h') {
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  }
  return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
}
